import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ChartCard from "../../components/ChartCard";
import HeroFigure from "../../components/HeroFigure";
import SegmentedToggle from "../../components/SegmentedToggle";
import StatTile from "../../components/StatTile";
import HorizontalBars from "../../components/charts/HorizontalBars";
import StackedColumns, { EmptyChartNote } from "../../components/charts/StackedColumns";
import TrendLine from "../../components/charts/TrendLine";
import { CardGrid, NoteCard, Section } from "../../components/Layout";
import { useDashboardData } from "../../hooks/useDashboardData";
import { fetchPlatformRevenue } from "../../dashboardApi";
import {
  formatCents,
  formatCentsCompact,
  formatDayTick,
  formatNumber,
  formatUtcDayKey,
} from "../../format";
import { dashboardPath } from "../../routes";
import { colors, PLAN_COLORS, PLAN_ORDER, viz, vizRamp } from "../../styles";
import {
  OverviewResponse,
  PlatformRevenueResponse,
  PlatformStatsResponse,
} from "../../types";
import { useIsNetBasis } from "../../revenueBasis";
import {
  GRANULARITIES,
  Granularity,
  bucketRevenue,
  formatBucketLabel,
  formatBucketTick,
  isPartialBucket,
  windowDays,
} from "../../volume";

const PLAN_SERIES = PLAN_ORDER.map(plan => ({
  key: plan,
  label: plan,
  color: PLAN_COLORS[plan],
}));

/**
 * Revenue series. Slot assignment is fixed dashboard-wide — subscription is
 * blue, ticketing orange, other fees yellow — so the same colour means the same
 * thing on every chart. See styles.ts.
 */
const REVENUE_SERIES = [
  { key: "subscription", label: "Subscription", color: viz.series1 },
  { key: "ticketing", label: "Ticketing fees", color: viz.series2 },
  { key: "other", label: "Other fees", color: viz.series4 },
];

const CHURN_SERIES = [
  { key: "canceled", label: "Canceled", color: viz.critical },
];

/**
 * The three lines on the recurring-revenue chart.
 *
 * **Colour is doing real work here and it is not a categorical assignment.**
 * Baseline and overflow are not two categories of revenue — they are the two
 * halves of ONE subscription, billed on one invoice through
 * `SubscriptionProduct.BASELINE`/`OVERFLOW`. So they take two steps of the
 * dashboard's sequential blue ramp rather than two categorical slots: same hue
 * family reads as "same thing, two components", which is exactly what they are.
 * Baseline keeps `viz.series1` (`vizRamp[0]`, the identical hex), which is this
 * dashboard's fixed slot for subscription revenue everywhere else.
 *
 * The total takes `colors.ink` deliberately, because it is not a category at
 * all — it is the sum of the other two, and giving it a categorical slot would
 * claim it is a third kind of revenue sitting alongside them. It is also the
 * line a reader looks at first, and ink is the strongest mark on a white card.
 */
const RECURRING_SERIES = [
  { key: "totalCents", label: "Total recurring", color: colors.ink },
  { key: "subscriptionCents", label: "Baseline subscription", color: vizRamp[0] },
  { key: "overflowCents", label: "Overflow (metered)", color: vizRamp[3] },
];

/**
 * Started and canceled are the two halves of one good/bad axis, so they wear
 * the status palette rather than categorical slots — the same reasoning that
 * gives delivered/failed their colors in ChannelSection.
 */
const STARTED_SERIES = [
  { key: "started", label: "Started", color: viz.good },
];

/** "2026-09" → "Sep 2026". */
const monthLabel = (month: string): string => {
  const date = new Date(`${month}-01T00:00:00.000Z`);
  if (isNaN(date.getTime())) return month;
  return date.toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};

const MonetizationSection = ({
  overview,
  platform,
  platformError,
  dimmed,
}: {
  overview: OverviewResponse;
  platform: PlatformStatsResponse | undefined;
  platformError: string | undefined;
  dimmed: boolean;
}) => {
  const navigate = useNavigate();
  const { monetization } = overview;
  const isNet = useIsNetBasis();

  const syncedAt = platform?.sync.lastRunFinishedAt ?? undefined;

  // Realized recurring revenue, whole history. Fetched here rather than in
  // HomeScreen because only this section draws it, so a failure degrades one
  // card instead of the tab — the same reasoning ChannelSection uses for its
  // Twilio call.
  //
  // Deliberately NOT scoped to the page's date range. This chart's question is
  // "how has recurring revenue moved over five years", the backing read is the
  // same cost whatever slice you want, and its own granularity toggle picks the
  // window — exactly the contract the platform volume chart already runs on.
  const revenueFetcher = useCallback(() => fetchPlatformRevenue(), []);
  const revenue = useDashboardData<PlatformRevenueResponse>(revenueFetcher, []);

  // Opens on MONTH, unlike the volume chart's day. A subscription invoice lands
  // on each customer's own billing anniversary rather than being spread evenly,
  // so the day series is genuinely lumpy — a spike is one customer renewing, not
  // a business event. Month is the granularity at which this series answers the
  // question it exists for.
  const [recurringGranularity, setRecurringGranularity] =
    useState<Granularity>("month");

  const recurringBuckets = useMemo(
    () =>
      bucketRevenue(
        windowDays(revenue.data?.days ?? [], recurringGranularity),
        recurringGranularity
      ),
    [revenue.data, recurringGranularity]
  );

  const newestRecurring = recurringBuckets[recurringBuckets.length - 1];
  const recurringPartial = isPartialBucket(newestRecurring, recurringGranularity);

  // On the net basis every line loses its own stream's Stripe fee, and a
  // bucket that is not FULLY measured is drawn as a gap rather than netted
  // partially — see `RevenueBucket.feeComplete`. `undefined` is what Recharts
  // renders as a break in the line, which is exactly the treatment a bucket
  // whose fee nobody recorded should get.
  const recurringSeriesData = useMemo(
    () =>
      recurringBuckets.map(bucket => {
        if (!isNet) return bucket;
        if (!bucket.feeComplete) {
          return {
            ...bucket,
            totalCents: undefined,
            subscriptionCents: undefined,
            overflowCents: undefined,
          };
        }
        const subscriptionCents = bucket.subscriptionCents - bucket.subscriptionFeeCents;
        const overflowCents = bucket.overflowCents - bucket.overflowFeeCents;
        return {
          ...bucket,
          subscriptionCents,
          overflowCents,
          totalCents: subscriptionCents + overflowCents,
        };
      }),
    [recurringBuckets, isNet]
  );

  /** Buckets a net reading can actually be drawn for. */
  const recurringNetBuckets = recurringBuckets.filter(bucket => bucket.feeComplete);

  const recurringShown = isNet
    ? recurringNetBuckets.reduce(
        (sum, bucket) =>
          sum +
          bucket.totalCents -
          bucket.subscriptionFeeCents -
          bucket.overflowFeeCents,
        0
      )
    : recurringBuckets.reduce((sum, bucket) => sum + bucket.totalCents, 0);

  // Daily rollup rows collapsed into calendar months. Overflow is folded into
  // subscription: both are what a creator pays Markit for the platform, and
  // splitting them here would imply overflow is a separate revenue line rather
  // than the metered part of the same subscription.
  const revenueByMonth = (() => {
    const months = new Map<
      string,
      { month: string; subscription: number; ticketing: number; other: number }
    >();
    (platform?.days ?? []).forEach(day => {
      const key = day.date.slice(0, 7);
      const row =
        months.get(key) ??
        { month: key, subscription: 0, ticketing: 0, other: 0 };
      // Every series answers "what did Markit keep", so refunds come off the
      // line they belong to rather than getting their own series. The two kinds
      // are netted at different points and must not both be applied here:
      // subscription refunds are already netted out by the rollup itself, while
      // the (rare) refunded application fee is still gross and is subtracted
      // below. Subtracting `subscriptionRefunds` here as well would double it.
      //
      // On the net basis each line additionally loses its OWN stream's Stripe
      // fee, so the stack still sums to what Markit kept. A day predating the
      // Sep 2026 fee capture carries no fee keys; `?? 0` is safe here ONLY
      // because `monthsWithoutFee` below counts those days and the card states
      // its own coverage — without that note this would quietly draw a
      // fee-free month as though it had been measured.
      row.subscription +=
        day.revenueCents.subscription +
        day.revenueCents.overflow -
        (isNet
          ? (day.revenueCents.subscriptionStripeFee ?? 0) +
            (day.revenueCents.overflowStripeFee ?? 0)
          : 0);
      row.ticketing +=
        day.revenueCents.ticketingFees -
        (isNet ? day.revenueCents.ticketingStripeFee ?? 0 : 0);
      row.other +=
        day.revenueCents.otherFees -
        day.revenueCents.refundedFees -
        (isNet ? day.revenueCents.otherStripeFee ?? 0 : 0);
      months.set(key, row);
    });
    // Array.from, not spread — this repo's tsconfig target predates
    // downlevelIteration, so spreading a Map iterator doesn't compile.
    return Array.from(months.values()).sort((a, b) =>
      a.month.localeCompare(b.month)
    );
  })();

  /**
   * Days in the range with revenue but no Stripe fee recorded.
   *
   * Stated on the revenue-by-month card rather than silently absorbed: those
   * days net out to their gross figure, so a month containing them understates
   * the fee. Goes to zero for any range inside the fee-capture era, and goes
   * to zero everywhere once the pending historical backfill runs.
   */
  const daysWithoutFee = (platform?.days ?? []).filter(
    day => day.revenueCents.subscriptionStripeFee === undefined
  ).length;

  const churnByDay = (platform?.days ?? []).map(day => ({
    date: day.date,
    canceled: day.subscriptionsCanceled,
  }));
  const totalChurned = churnByDay.reduce((sum, day) => sum + day.canceled, 0);

  // Stripe's own count of subscriptions created on each day, already written by
  // the nightly rollup. This is NOT Mixpanel's "Subscriptions Started" event —
  // it counts subscription objects on the live Stripe account, so the card says
  // so rather than letting the two be quoted interchangeably.
  const startedByDay = (platform?.days ?? []).map(day => ({
    date: day.date,
    started: day.subscriptionsStarted,
  }));
  const totalStarted = startedByDay.reduce((sum, day) => sum + day.started, 0);

  // One row per state, one stacked segment per plan — so "how many Pro
  // customers are still on trial" is readable without a second chart.
  const planRows = [
    {
      state: "Active",
      ...Object.fromEntries(
        monetization.planBreakdown.map(row => [row.plan, row.active])
      ),
    },
    {
      state: "Trial",
      ...Object.fromEntries(
        monetization.planBreakdown.map(row => [row.plan, row.trial])
      ),
    },
  ];

  return (
    <Section
      title="Monetization"
      description="Subscription state is live from Firestore; revenue and churn come from the nightly Stripe rollup. Every card says which."
    >
      <CardGrid min={220}>
        {/*
          Overflow is INCLUDED here since Sep 2026, and that changed the figure
          by more than it changed the label. Measured across the 71 ACTIVE
          customers when it was wired in: trailing-30d overflow $12,481 against
          $3,417 of baseline subscription revenue — 3.7x. The old "excludes
          overflow" figure was reporting roughly a fifth of Markit's recurring
          subscription revenue under a headline called MRR.

          The tile now wears the NIGHTLY clock rather than a green "Live" dot.
          Its baseline half is still live from Firestore, but its overflow half
          comes from the rollup's trailing windows — and a number that is partly
          last night's wearing a Live chip is exactly the mismatch the required
          `freshness` prop exists to catch. The staler of the two halves wins.
        */}
        <HeroFigure
          label={isNet ? "Estimated MRR, net of Stripe fees" : "Estimated MRR"}
          value={
            !isNet
              ? formatCents(monetization.estimatedMrrCents)
              : monetization.estimatedNetMrrCents === null
              ? "—"
              : formatCents(monetization.estimatedNetMrrCents)
          }
          subtitle={
            isNet
              ? monetization.stripeFee.rate === null
                ? "No realized subscription revenue in the last 90 days to measure a Stripe rate from"
                : `${formatCents(
                    monetization.estimatedMrrCents
                  )} gross, less Markit's realized ${(
                    monetization.stripeFee.rate * 100
                  ).toFixed(1)}% Stripe rate · excludes trials`
              : `${formatCents(
                  monetization.baselineMrrCents
                )} plan baselines + ${formatCents(
                  monetization.overflow.mrrCents
                )} projected overflow · excludes trials`
          }
          freshness={{
            kind: "cached",
            updatedAt: monetization.overflow.sync.lastRunFinishedAt ?? undefined,
          }}
        />
        <StatTile
          label="Paying customers"
          value={formatNumber(monetization.payingCustomers)}
          secondary="Subscription state ACTIVE"
          freshness={{ kind: "live" }}
        />
        <StatTile
          label="Trials in progress"
          value={formatNumber(monetization.trials)}
          secondary={`${formatNumber(monetization.trialsEndingSoon)} end within ${monetization.trialEndingWindowDays} days`}
          freshness={{ kind: "live" }}
        />
        <StatTile
          label="Canceling at period end"
          value={formatNumber(monetization.cancelAtEnd)}
          tone={monetization.cancelAtEnd > 0 ? "warning" : "neutral"}
          secondary="View in Customers →"
          title="Open the customer list filtered to cancelAtEnd"
          onClick={() => navigate(`${dashboardPath.customers()}?canceling=1`)}
          freshness={{ kind: "live" }}
        />
        <StatTile
          label="Payment failed"
          value={formatNumber(monetization.paymentFailed)}
          tone={monetization.paymentFailed > 0 ? "critical" : "neutral"}
          secondary="Subscription state PAYMENT_FAILED"
          freshness={{ kind: "live" }}
        />
      </CardGrid>

      <div style={{ marginTop: 16 }}>
        <NoteCard title="What “Estimated MRR” includes, and why overflow is a projection">
          Two halves, on two clocks.{" "}
          <strong>{formatCents(monetization.baselineMrrCents)}</strong> is the sum of
          active customers&rsquo; plan <strong>list prices</strong>, read live — still an
          &ldquo;estimate&rdquo; because a list price ignores proration, tax and mid-cycle
          upgrades. <strong>{formatCents(monetization.overflow.mrrCents)}</strong> is
          projected <strong>overflow</strong>, the metered per-message charge, and it has
          to be projected: overflow is billed on usage, so unlike a baseline plan there is
          no committed next-month amount to read anywhere — only what it actually cost
          after the fact. That half comes from the nightly rollup, which is why this card
          shows the sync&rsquo;s clock rather than a Live chip.{" "}
          <strong>Overflow was excluded entirely until Sep 2026</strong>, and it is not a
          rounding item — it currently runs about{" "}
          {monetization.baselineMrrCents > 0
            ? `${(
                monetization.overflow.trailing30dCents / monetization.baselineMrrCents
              ).toFixed(1)}x`
            : "several times"}{" "}
          the baseline figure beside it.
          <br />
          <br />
          The projection is{" "}
          <strong>
            half the last 30 days plus half the last 90 days&rsquo; monthly rate
          </strong>{" "}
          ({formatCents(monetization.overflow.trailing30dCents)} and{" "}
          {formatCents(monetization.overflow.trailing90dCents)} respectively), across the{" "}
          {formatNumber(monetization.overflow.customersWithOverflow)} active customers
          with any realized overflow. Blended rather than a plain 30-day figure because
          30 days alone measured <em>worst</em> of the three candidates in a 12-month
          out-of-sample backtest — 19.9% median error against 15.0% for this blend —
          while a pure 90-day average lags a growing series, and Markit&rsquo;s overflow
          nearly tripled in 14 months. Trials are excluded from both halves, for the same
          reason: they are not billing yet.
          {monetization.overflow.customersWithoutStats > 0 && (
            <>
              {" "}
              <strong>
                {formatNumber(monetization.overflow.customersWithoutStats)} active
                customer
                {monetization.overflow.customersWithoutStats === 1 ? " has" : "s have"} no
                rollup document yet
              </strong>
              , so the overflow half is an undercount by whatever they bill.
            </>
          )}
        </NoteCard>
      </div>

      {isNet && (
        <div style={{ marginTop: 12 }}>
          <NoteCard title="Net MRR is a rate, not a measured fee — the only figure here that is">
            Every other net number on this dashboard subtracts a fee Stripe
            actually charged, read per charge from{" "}
            <code>balance_transaction.fee</code>. Estimated MRR cannot work that
            way: it is a projection of revenue that <em>has not been billed
            yet</em>, and a payment Stripe has not processed has no fee to read.
            So this applies Markit&rsquo;s own <strong>realized</strong> Stripe
            rate on subscription revenue &mdash;{" "}
            {monetization.stripeFee.rate === null ? (
              <>
                which cannot be measured yet, because no day in the last{" "}
                {monetization.stripeFee.daysInWindow} has Stripe&rsquo;s cut
                recorded. The historical fee backfill runs after this ships, and
                until it does no net MRR is shown
              </>
            ) : (
              <>
                <strong>
                  {(monetization.stripeFee.rate * 100).toFixed(2)}%
                </strong>
                , being {formatCents(monetization.stripeFee.feeCents)} of fees on{" "}
                {formatCents(monetization.stripeFee.revenueCents)} of realized
                subscription revenue
              </>
            )}
            .{" "}
            {monetization.stripeFee.daysWithFee > 0 &&
              monetization.stripeFee.daysWithFee <
                monetization.stripeFee.daysInWindow && (
                <>
                  <strong>
                    Measured over {monetization.stripeFee.daysWithFee} of the
                    last {monetization.stripeFee.daysInWindow} days
                  </strong>{" "}
                  &mdash; only those days have a fee recorded so far, and both
                  sides of the ratio are restricted to them so the rate stays
                  self-consistent rather than being diluted by days nobody
                  measured. The pending backfill widens this to the full window.
                </>
              )}
            <br />
            <br />
            A measured rate rather than Stripe&rsquo;s published one, because
            Markit&rsquo;s negotiated pricing is a percentage <em>plus</em> a
            fixed amount per charge &mdash; so the effective rate moves with
            plan price (about 4.8% on a $10 plan, about 3.1% on a $40 one) and a
            hardcoded number would go stale silently. Scoped to subscription
            revenue only: MRR contains no ticketing, and ticketing carries a far
            heavier rate because Markit is merchant of record on the whole
            ticket price.
          </NoteCard>
        </div>
      )}

      {/*
        The historical companion to the Estimated MRR tile above — and it is
        deliberately a DIFFERENT measurement, not that tile plotted backwards.

        There is no historical MRR series and there cannot be one. `mrrCents` on
        a rollup row is point-in-time, and it is deliberately absent from every
        backfilled row: stamping one reading across five years would draw a flat
        line out of a single measurement. Verified against the live collection
        before this chart was built — 4 of 1,743 rows carry it.

        What is complete is what Markit actually INVOICED each day, and this
        draws that: baseline plus metered overflow, both populated on 1,743 of
        1,743 rows back to 2021-12-01. Realized revenue looking backwards versus
        a projection looking forwards — related, and not the same number, which
        is why the title says "realized" and the note below spells out the
        difference rather than leaving a reader to assume the two should tie out.
      */}
      <div style={{ marginTop: 16 }}>
        <ChartCard
          title={`Realized recurring revenue over time${
            isNet ? ", net of Stripe fees" : ""
          }`}
          subtitle={
            recurringBuckets.length === 0
              ? "Nightly Stripe rollup · what was actually invoiced, net of refunds"
              : isNet
              ? `Nightly Stripe rollup · invoiced, net of refunds and Stripe's cut · ${formatCents(
                  recurringShown
                )} across the ${recurringNetBuckets.length} of ${
                  recurringBuckets.length
                } buckets with a fee recorded`
              : `Nightly Stripe rollup · what was actually invoiced, net of refunds · ${formatCents(
                  recurringShown
                )} shown`
          }
          series={RECURRING_SERIES}
          freshness={{
            kind: "cached",
            updatedAt: revenue.data?.sync.lastRunFinishedAt ?? undefined,
          }}
          dimmed={revenue.refreshing}
          action={
            <SegmentedToggle
              label="Granularity"
              options={GRANULARITIES}
              value={recurringGranularity}
              onChange={setRecurringGranularity}
            />
          }
          table={{
            categoryLabel:
              recurringGranularity === "day"
                ? "Day"
                : recurringGranularity === "week"
                ? "Week of"
                : "Month",
            // Reads off the same array the chart draws, so the table cannot
            // show a figure for a bucket the line breaks over. An unmeasured
            // bucket prints "—", the standing "— is not zero" rule.
            rows: recurringSeriesData.map(bucket => ({
              category: formatBucketTick(bucket.date, recurringGranularity),
              values: {
                totalCents:
                  bucket.totalCents === undefined
                    ? "—"
                    : formatCents(bucket.totalCents),
                subscriptionCents:
                  bucket.subscriptionCents === undefined
                    ? "—"
                    : formatCents(bucket.subscriptionCents),
                overflowCents:
                  bucket.overflowCents === undefined
                    ? "—"
                    : formatCents(bucket.overflowCents),
              },
            })),
          }}
        >
          {revenue.error ? (
            <EmptyChartNote label={revenue.error} />
          ) : revenue.loading ? (
            <EmptyChartNote label="Loading…" />
          ) : recurringBuckets.length === 0 ? (
            <EmptyChartNote label="The nightly sync hasn't written any revenue rows yet." />
          ) : isNet && recurringNetBuckets.length === 0 ? (
            <EmptyChartNote label="No bucket in this window has Stripe's cut recorded yet, so there is no net series to draw. The historical fee backfill runs after this ships." />
          ) : (
            <TrendLine
              data={recurringSeriesData}
              series={RECURRING_SERIES}
              categoryKey="date"
              height={220}
              tickFormatter={value => formatBucketTick(value, recurringGranularity)}
              labelFormatter={value => formatBucketLabel(value, recurringGranularity)}
              // These series are CENTS. Without this the axis reads a hundred
              // times too large — the same bug that had a revenue chart printing
              // "800000" for $8,000.
              valueTickFormatter={formatCentsCompact}
              valueFormatter={value => formatCents(value, true)}
            />
          )}
        </ChartCard>
      </div>

      <div style={{ marginTop: 12 }}>
        <NoteCard title="This is realized revenue, not the Estimated MRR figure plotted backwards">
          The tile above answers <em>what recurs next month</em> — live plan list
          prices plus a projection of metered overflow. This chart answers{" "}
          <em>what Markit actually billed</em>, day by day, from the nightly Stripe
          rollup. They are related and they are not the same measurement, so they
          will not tie out and are not meant to.{" "}
          <strong>
            There is no historical Estimated MRR series and there cannot be one
          </strong>
          : the stored MRR reading is point-in-time and is deliberately left off every
          backfilled row, because one measurement stamped across five years draws a
          flat line that looks like history and is not. Realized revenue has no such
          problem — every day was invoiced on the day it is charted.
          <br />
          <br />
          <strong>Total</strong> is the sum of the two lines below it, derived here so
          the three can never disagree. <strong>Baseline</strong> is the plan charge;{" "}
          <strong>overflow</strong> is the metered per-message charge on the same
          subscription, billed on the same invoice — two halves of one thing, which is
          why they share a colour family rather than taking separate categorical slots.
          Both are net of subscription refunds, subtracted at source and split across
          the two lines in the same proportion as the payment. Ticketing and other
          application fees are <em>not</em> here: they are not recurring, and the
          revenue-by-month chart below carries them.
          {revenue.data?.recurringFrom && revenue.data?.windowFrom && (
            <>
              <br />
              <br />
              The series starts at{" "}
              <strong>{formatUtcDayKey(revenue.data.windowFrom)}</strong>, and the flat
              stretch before{" "}
              <strong>{formatUtcDayKey(revenue.data.recurringFrom)}</strong> is a{" "}
              <strong>real zero, not missing data</strong> — every day from the start of
              the window has been processed, and Markit was not billing subscriptions
              yet.
              {revenue.data.overflowFrom && (
                <>
                  {" "}
                  Overflow is flat for longer still: it carries nothing until{" "}
                  <strong>{formatUtcDayKey(revenue.data.overflowFrom)}</strong>, which is
                  that revenue line&rsquo;s real age rather than a windowing choice.
                </>
              )}
            </>
          )}
          {(revenue.data?.missingDays.length ?? 0) > 0 && (
            <>
              {" "}
              <strong>
                {formatNumber(revenue.data!.missingDays.length)} day
                {revenue.data!.missingDays.length === 1 ? "" : "s"}
              </strong>{" "}
              in the window {revenue.data!.missingDays.length === 1 ? "has" : "have"} no
              rollup row at all and{" "}
              {revenue.data!.missingDays.length === 1 ? "is" : "are"} absent from the
              lines rather than drawn as $0, which would read as a day Markit earned
              nothing. Oldest: {revenue.data!.missingDays[0]}.
            </>
          )}
        </NoteCard>
      </div>

      {recurringPartial && newestRecurring && recurringBuckets.length > 1 && (
        <div style={{ marginTop: 12 }}>
          <NoteCard title={`The last ${recurringGranularity} is still in progress`}>
            {formatBucketLabel(newestRecurring.date, recurringGranularity)} covers{" "}
            {newestRecurring.dayCount} day
            {newestRecurring.dayCount === 1 ? "" : "s"} so far, not a full{" "}
            {recurringGranularity}, because the nightly rollup stops at yesterday. Its
            point is a running total and will keep climbing — it is not a drop in
            revenue.
          </NoteCard>
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        <CardGrid min={340}>
          <ChartCard
            title="Customers by plan"
            subtitle="Firestore · users.customer.state + current baseline price"
            series={PLAN_SERIES}
            dimmed={dimmed}
            freshness={{ kind: "live" }}
            table={{
              categoryLabel: "Status",
              rows: planRows.map(row => ({
                category: row.state,
                values: Object.fromEntries(
                  PLAN_ORDER.map(plan => [plan, Number((row as any)[plan] ?? 0)])
                ),
              })),
            }}
          >
            <HorizontalBars
              data={planRows}
              series={PLAN_SERIES}
              categoryKey="state"
              height={150}
            />
          </ChartCard>

          <ChartCard
            title={`Markit revenue by month${isNet ? " (net)" : ""}`}
            subtitle={
              isNet
                ? `Nightly Stripe rollup · subscription invoices + ticketing and other fees, net of refunds and Stripe's cut${
                    daysWithoutFee > 0
                      ? ` · ${daysWithoutFee} day${
                          daysWithoutFee === 1 ? "" : "s"
                        } in range have no fee recorded and net to their gross figure`
                      : ""
                  }`
                : "Nightly Stripe rollup · subscription invoices + ticketing and other fees, net of refunds"
            }
            series={REVENUE_SERIES}
            dimmed={dimmed}
            freshness={{ kind: "cached", updatedAt: syncedAt }}
            table={{
              categoryLabel: "Month",
              rows: revenueByMonth.map(row => ({
                category: monthLabel(row.month),
                values: {
                  subscription: formatCents(row.subscription),
                  ticketing: formatCents(row.ticketing),
                  other: formatCents(row.other),
                },
              })),
            }}
          >
            {platformError ? (
              <EmptyChartNote label={platformError} />
            ) : revenueByMonth.length === 0 ? (
              <EmptyChartNote label="No rollup data for this range yet." />
            ) : (
              <StackedColumns
                data={revenueByMonth}
                series={REVENUE_SERIES}
                categoryKey="month"
                tickFormatter={monthLabel}
                labelFormatter={monthLabel}
                // These series are CENTS — without this the axis reads a
                // hundred times too large.
                valueTickFormatter={formatCentsCompact}
              />
            )}
          </ChartCard>
        </CardGrid>
      </div>

      <div style={{ marginTop: 16 }}>
        <CardGrid min={280}>
          <ChartCard
            title="Subscriptions started"
            subtitle={`Nightly Stripe rollup · ${formatNumber(
              totalStarted
            )} started in this range`}
            series={STARTED_SERIES}
            dimmed={dimmed}
            freshness={{ kind: "cached", updatedAt: syncedAt }}
            table={{
              categoryLabel: "Day",
              rows: startedByDay.map(row => ({
                category: row.date,
                values: { started: row.started },
              })),
            }}
          >
            {platformError ? (
              <EmptyChartNote label={platformError} />
            ) : startedByDay.length === 0 ? (
              <EmptyChartNote label="No rollup data for this range yet." />
            ) : (
              <TrendLine
                data={startedByDay}
                series={STARTED_SERIES}
                categoryKey="date"
                height={110}
                tickFormatter={formatDayTick}
                labelFormatter={formatDayTick}
              />
            )}
          </ChartCard>
          <ChartCard
            title="Churned subscriptions"
            subtitle={`Nightly Stripe rollup · ${formatNumber(
              totalChurned
            )} canceled in this range`}
            series={CHURN_SERIES}
            dimmed={dimmed}
            freshness={{ kind: "cached", updatedAt: syncedAt }}
            table={{
              categoryLabel: "Day",
              rows: churnByDay.map(row => ({
                category: row.date,
                values: { canceled: row.canceled },
              })),
            }}
          >
            {platformError ? (
              <EmptyChartNote label={platformError} />
            ) : churnByDay.length === 0 ? (
              <EmptyChartNote label="No rollup data for this range yet." />
            ) : (
              <TrendLine
                data={churnByDay}
                series={CHURN_SERIES}
                categoryKey="date"
                height={110}
                tickFormatter={formatDayTick}
                labelFormatter={formatDayTick}
              />
            )}
          </ChartCard>
        </CardGrid>
      </div>
    </Section>
  );
};

export default MonetizationSection;
